import { EmployeeDTO, SearchPeopleLiveRow } from "@shaarp/common";

/**
 * A person found by a search still running, as a row of the employees table:
 * the profile only — the email and phone are revealed from the saved table.
 */
export const liveRowToEmployee = (row: SearchPeopleLiveRow): EmployeeDTO =>
  ({
    ...row,
    domain: row.domain ?? "",
    employedFrom: row.employedFrom ? new Date(row.employedFrom) : null,
    level: row.level ?? "DEFAULT",
    email: null,
    emailStatus: "UNKNOWN",
    phone: null,
    phoneStatus: "UNKNOWN",
    suggestedOrder: null,
    suggestedReason: null,
    integrations: {},
    companyId: row.companyId ?? undefined,
    companyName: row.companyName ?? undefined,
  }) satisfies EmployeeDTO;

const isSearchId = (value: unknown): value is string =>
  typeof value === "string" && value.length > 0;

/**
 * Whether an output holds what its status promises the cards and the panel
 * read: the search a table belongs to, the rows a running search streams (a
 * stored one has none), the reason of a refusal.
 */
const isWellFormedPeopleToolOutput = (
  output: Record<string, unknown>,
): boolean => {
  switch (output.status) {
    case "running":
      // The rows are streamed, never stored: a running search read back
      // from the thread has none (`reducePeopleSearchOutput` on the API).
      return (
        isSearchId(output.searchId) &&
        (output.liveRows === undefined || Array.isArray(output.liveRows))
      );
    case "done":
      return isSearchId(output.searchId);
    case "refused":
      return typeof output.reason === "string";
    default:
      return false;
  }
};

/**
 * The output of `searchPeople` as the thread receives it, or null while it is not one yet — or no longer one: an
 * output read back from a conversation, which lost a field on the way, would
 * otherwise open the table of search "".
 */
export const readPeopleToolOutput = <Output extends { status: string }>(
  output: unknown,
): Output | null =>
  output &&
  typeof output === "object" &&
  isWellFormedPeopleToolOutput(output as Record<string, unknown>)
    ? (output as Output)
    : null;
